import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import ComingSoonHomeBanner from '../components/ComingSoonHomeBanner';
import HeroFilm from '../components/HeroFilm';
import HowItWorksSection from '../components/HowItWorksSection';
import MediaGlobe from '../components/MediaGlobe';
import Reveal from '../components/Reveal';
import ServiceDeck from '../components/ServiceDeck';
import { useLanguage } from '../context/LanguageContext';
import { getYouTubeEmbedUrl } from '../utils/getYouTubeEmbedUrl';

// Temporarily hidden while reviewing the layout — flip to true to bring it back.
const SHOW_COMING_SOON_BANNER = false;

// Welcome-section image per language. Previous photo link: content/previousImages.js
const introImages = {
  ar: `${process.env.PUBLIC_URL}/images/MainAndPartners/main_arabic.jfif`,
  en: `${process.env.PUBLIC_URL}/images/MainAndPartners/main_en.jfif`,
};

// A film uploaded with the site plays straight from public/videos, so nothing can block it and
// the quality is the file's own. Languages without one fall back to the YouTube links below.
const HERO_VIDEO_FILES = {
  ar: `${process.env.PUBLIC_URL}/videos/arabic.mp4`,
  en: `${process.env.PUBLIC_URL}/videos/english.mp4`,
};

// The "Philosophy of Holol Real Estate Club" film for each language plays silently above the
// opening; the same links are in homeIntroVideo in the translations.
const HERO_FILMS = {
  ar: 'https://www.youtube.com/watch?v=PWfCjYRi_dU',
  en: 'https://youtu.be/S76qRSo1su4',
};

// Pictures for the two exclusive ideas. Previous photo links: content/previousImages.js
const ideaImages = {
  certificate: `${process.env.PUBLIC_URL}/images/newIdeas/certificateMain.jfif`,
  supermarket: `${process.env.PUBLIC_URL}/images/newIdeas/supermarketMain.jfif`,
};

// The founder's picture, one per language (the Arabic one carries his name in Arabic).
const founderImages = {
  ar: `${process.env.PUBLIC_URL}/images/founderArabic.png`,
  en: `${process.env.PUBLIC_URL}/images/founder.png`,
};

// Services shown in the home page deck (catalogue numbers).
const HOME_DECK_SERVICE_IDS = ['01', '02', '04', '14', '17', '18', '20'];

function HeroOutlineCta({ href, children }) {
  const external = /^https?:\/\//i.test(href);
  if (external) {
    return (
      <a className="ghost-btn" href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return (
    <NavLink className="ghost-btn" to={href}>
      {children}
    </NavLink>
  );
}

function HomePage() {
  const { t, language } = useLanguage();
  const homeIntroVideoEmbed = useMemo(
    () => getYouTubeEmbedUrl(t.homeIntroVideo?.videoUrl),
    [t.homeIntroVideo?.videoUrl]
  );
  const heroVideoFile = HERO_VIDEO_FILES[language] || null;
  // The film in the page's language (HeroFilm plays it muted, looping and without controls).
  const heroFilmId = useMemo(() => {
    const embed = getYouTubeEmbedUrl(HERO_FILMS[language] || HERO_FILMS.en);
    return embed ? embed.slice(embed.lastIndexOf('/') + 1) : null;
  }, [language]);
  const groups = t.servicesCatalog?.groups || [];
  const sovereign = t.servicesCatalog?.sovereign;
  const services = [...groups.flatMap((group) => group.items), ...(sovereign ? [sovereign] : [])];
  // The globe's orbiting badges: the nine channels from the marketing page.
  const mediaChannels = useMemo(
    () => t.marketingPage.items.map(({ icon, title }) => ({ icon, label: title })),
    [t.marketingPage.items]
  );
  const previewServices = services.filter((service) => HOME_DECK_SERVICE_IDS.includes(service.id));

  return (
    <>
      <header id="hero" className="hero">
        {/* The Philosophy film plays silently above the opening, with the club mark on it. */}
        <div className="hero-films" aria-hidden="true">
          {heroVideoFile ? (
            <div className="hero-film">
              <video
                key={heroVideoFile}
                src={heroVideoFile}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                tabIndex={-1}
              />
            </div>
          ) : heroFilmId ? (
            <HeroFilm videoId={heroFilmId} title={t.homeIntroVideo.title} />
          ) : null}
          <div className="hero-films-mark">
            <img src={`${process.env.PUBLIC_URL}/images/realestateclublogo.png`} alt="" />
            <span>{t.hero.videoCaption}</span>
          </div>
        </div>

        <div className="container hero-panel-wrap">
          <Reveal>
            <div className="hero-panel">
              <span className="badge">{t.hero.badge}</span>
              <h1>{t.hero.title}</h1>
              <p>{t.hero.subtitle}</p>
              <div className="hero-actions">
                <HeroOutlineCta href="/services">{t.hero.explore}</HeroOutlineCta>
                <HeroOutlineCta href={t.hero.latestProjectsHref}>{t.hero.latestProjects}</HeroOutlineCta>
                <HeroOutlineCta href={t.hero.previousWorkHref}>{t.hero.previousWork}</HeroOutlineCta>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="container hero-stats">
          {t.stats.map((s, i) => (
            <Reveal key={s.label} delay={200 + i * 80}>
              <div className="stat-card">
                <span className="stat-value">{s.value}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </header>

      {/* <section className="section hook-sections">
        <div className="container hook-grid">
          <Reveal>
            <article className="hook-card">
              <h3>{t.visualHook.revolutionTitle}</h3>
              <p>{t.visualHook.revolutionText}</p>
            </article>
          </Reveal>
          <Reveal delay={80}>
            <article className="hook-card">
              <h3>{t.visualHook.mediaEngineTitle}</h3>
              <p>{t.visualHook.mediaEngineText}</p>
            </article>
          </Reveal>
          <Reveal delay={120}>
            <article className="hook-card">
              <h3>{t.visualHook.uspsTitle}</h3>
              <ul className="hook-list">
                {t.visualHook.uspsItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          </Reveal>
          <Reveal delay={160}>
            <article className="hook-card">
              <h3>{t.visualHook.noCommissionTitle}</h3>
              <p>{t.visualHook.noCommissionText}</p>
            </article>
          </Reveal>
          <Reveal delay={200}>
            <article className="hook-card">
              <h3>{t.visualHook.partnersTitle}</h3>
              <div className="partner-chips">
                {t.visualHook.partners.map((partner) => (
                  <span key={partner}>{partner}</span>
                ))}
              </div>
            </article>
          </Reveal>
          <Reveal delay={240}>
            <article className="hook-card">
              <h3>{t.visualHook.expoTitle}</h3>
              <p>{t.visualHook.expoText}</p>
            </article>
          </Reveal>
        </div>
      </section> */}

      <section id="about" className="section intro">
        <div className="container intro-grid">
          <Reveal direction="left">
            <div>
              <span className="eyebrow">{t.intro.eyebrow}</span>
              <div className="accent-line" />
              <h2>{t.intro.title}</h2>
              {(t.intro.pairs || []).map((pair) => (
                <div key={`${pair.leadGold}-${pair.leadRest}`} className="intro-pair">
                  <p className="intro-pair-title">
                    <span className="intro-pair-lead-rest">{pair.leadRest}</span>
                  </p>
                  <p>{pair.body}</p>
                </div>
              ))}
              <NavLink to="/strategy" className="primary-btn intro-cta-btn">
                {t.intro.link}
              </NavLink>
            </div>
          </Reveal>
          <Reveal direction="right">
            <div className="intro-image-wrap">
              <img
                className="intro-image"
                src={introImages[language] || introImages.en}
                alt={t.intro.title}
                width={1408}
                height={768}
                decoding="async"
              />
              <div className="intro-image-accent" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section home-intro-video" aria-labelledby="home-intro-video-heading">
        <div className="container home-intro-video-inner">
          <Reveal>
            <h2 id="home-intro-video-heading" className="home-intro-video-title">
              {t.homeIntroVideo.title}
            </h2>
          </Reveal>
          {homeIntroVideoEmbed ? (
            <Reveal delay={100}>
              <div className="home-intro-video-frame">
                <iframe
                  title={t.homeIntroVideo.title}
                  src={`${homeIntroVideoEmbed}?rel=0`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            </Reveal>
          ) : null}
        </div>
      </section>

      <HowItWorksSection />

      <section id="services" className="section services">
        <div className="container">
          <Reveal>
            <div className="services-head">
              <h2>{t.servicesHome.title}</h2>
              <p>{t.servicesHome.subtitle}</p>
            </div>
          </Reveal>
          {/* Cards use the services-page images. Previous photo links: content/previousImages.js */}
          <Reveal>
            <ServiceDeck
              services={previewServices}
              label={t.servicesHome.title}
              hint={t.servicesHome.deckHint}
              touchHint={t.servicesHome.deckHintTouch}
              moreLabel={t.serviceDetail?.knowMore || 'Learn more'}
            />
          </Reveal>
          <Reveal delay={200}>
            <div className="services-more-cta-wrap">
              <NavLink to="/services" className="primary-btn services-more-cta-btn">
                {t.servicesHome.learnMoreCta}
              </NavLink>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container home-marketing-preview">
        <Reveal>
          <h2 className="home-marketing-section-title">{t.marketingPage.sectionTitle}</h2>
        </Reveal>
        <div className="marketing-hero home-media-hero">
          {/* Replaced the three-photo mosaic (still used on the marketing page, see App.css). */}
          <Reveal direction="left">
            <div className="home-media-copy">
              <span className="home-media-eyebrow">
                <span className="material-symbols-outlined" aria-hidden="true">campaign</span>
                {t.marketingPage.homeEyebrow}
              </span>
              <h3 className="home-media-title">
                {t.marketingPage.titleLead}{' '}
                <span className="gold-shine">{t.marketingPage.titleHighlight}</span>
              </h3>
              <p className="home-media-text">{t.marketingPage.homeText}</p>
              <NavLink className="primary-btn home-media-btn" to="/marketing">
                {t.hero.marketing}
                <span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
              </NavLink>
            </div>
          </Reveal>
          <Reveal direction="right">
            <MediaGlobe label={t.marketingPage.globeLabel} channels={mediaChannels} />
          </Reveal>
        </div>
      </section>

      <section className="section membership-home">
        <div className="container membership-grid">
          <Reveal direction="left">
            <div className="membership-copy">
              <span className="eyebrow">{t.membershipHome.eyebrow}</span>
              <h2>{t.membershipHome.title}</h2>
              <p>{t.membershipHome.text}</p>
              <NavLink to="/audience" className="primary-btn membership-cta-btn">
                {t.membershipHome.cta}
                <span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
              </NavLink>
            </div>
          </Reveal>
          <Reveal direction="right">
            <div className="membership-image">
              <img src={`${process.env.PUBLIC_URL}/images/memberships.jfif`} alt={t.membershipHome.title} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section investment-home">
        <div className="container">
          <Reveal>
            <div className="investment-home-head">
              <span className="eyebrow">{t.investmentHome.eyebrow}</span>
              <h2>{t.investmentHome.title}</h2>
              <p>{t.investmentHome.subtitle}</p>
            </div>
          </Reveal>
          <div className="investment-home-grid">
            <Reveal direction="left">
              <div className="investment-home-copy">
                {t.investmentHome.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                <NavLink to="/investment" className="primary-btn investment-home-btn">
                  {t.investmentHome.cta}
                  <span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
                </NavLink>
              </div>
            </Reveal>
            <Reveal direction="right">
              <div className="investment-home-image">
                <img
                  src={`${process.env.PUBLIC_URL}/images/investment.jfif`}
                  alt={t.investmentHome.title}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section ideas-home">
        <div className="container">
          <Reveal>
            <div className="ideas-head">
              <span className="eyebrow">{t.ideasHome.eyebrow}</span>
              <h2>
                {t.ideasHome.titleLead} <span className="gold-shine">{t.ideasHome.titleHighlight}</span>
              </h2>
            </div>
          </Reveal>
          <div className="ideas-grid">
            <Reveal direction="left">
              <article className="idea-card idea-primary">
                <div className="idea-image" style={{ backgroundImage: `url(${ideaImages.certificate})` }} />
                <div className="idea-body">
                  <h3>{t.ideasHome.certificate.title}</h3>
                  <p>{t.ideasHome.certificate.text}</p>
                  <NavLink to="/ideas" className="primary-btn idea-cta-btn">
                    {t.ideasHome.cta}
                  </NavLink>
                </div>
              </article>
            </Reveal>
            <Reveal direction="right">
              <article className="idea-card idea-secondary">
                <div className="idea-image" style={{ backgroundImage: `url(${ideaImages.supermarket})` }} />
                <div className="idea-body">
                  <h3>{t.ideasHome.supermarket.title}</h3>
                  <p>{t.ideasHome.supermarket.text}</p>
                  <NavLink to="/ideas" className="primary-btn idea-cta-btn">
                    {t.ideasHome.cta}
                  </NavLink>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section home-founder">
        <div className="container">
          <Reveal>
            <div className="home-founder-head">
              <span className="eyebrow">{t.founderHome.eyebrow}</span>
              <h2>{t.founderHome.title}</h2>
              <p className="home-founder-name">{t.founderHome.name}</p>
            </div>
          </Reveal>
          <div className="home-founder-grid">
            <Reveal direction="left">
              <div className="home-founder-copy">
                <p className="home-founder-text">{t.founderHome.text}</p>
              </div>
            </Reveal>
            <Reveal direction="right">
              <div className="home-founder-image">
                <img src={founderImages[language] || founderImages.en} alt={t.founderHome.name} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="cta-banner">
        <div className="container cta-grid">
          <Reveal direction="left">
            <div className="cta-content">
              <h2>{t.ctaBanner.title}</h2>
              <p>{t.ctaBanner.subtitle}</p>
              <div className="hero-actions">
                <NavLink className="primary-btn primary-btn-light" to="/audience">
                  {t.ctaBanner.primary}
                </NavLink>
                <a className="ghost-btn" href="#contact">
                  {t.ctaBanner.secondary}
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal direction="right">
            <div className="cta-image">
              <img src={`${process.env.PUBLIC_URL}/images/joinus.jfif`} alt={t.ctaBanner.title} />
            </div>
          </Reveal>
        </div>
      </section>

      {SHOW_COMING_SOON_BANNER ? <ComingSoonHomeBanner /> : null}
    </>
  );
}

export default HomePage;
